import { BellRing, Flag, Hand, RefreshCw, Ruler, Smartphone, Sun, Users, Vibrate } from 'lucide-react';
import { HandicapCard } from './HandicapCard.tsx';
import { LicenceCard } from './LicenceCard.tsx';
import { useState } from 'react';
import type { HapticTest } from '../hooks/useHaptics.ts';
import { hapticsSupported, useHaptics } from '../hooks/useHaptics.ts';
import { wakeLockSupported } from '../hooks/useWakeLock.ts';
import { ApiError } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useNotifications } from '../lib/notifications.tsx';
import { useSettings } from '../lib/settings.tsx';
import type { UserSettings } from '../lib/settings.tsx';
import { useUpdates } from '../lib/updates.tsx';

/** Bouton d'un choix fermé. Plus lisible qu'une liste déroulante sur mobile,
 *  et surtout plus grand : ces réglages se touchent parfois gant au poignet. */
function Choice<T extends string>({ options, value, onPick, disabled }: {
    options: { value: T; label: string; hint?: string }[];
    value: T;
    onPick: (v: T) => void;
    disabled?: boolean;
}) {
    // Classes littérales et non construites : Tailwind lit le source pour
    // décider quoi générer, une classe assemblée à l'exécution n'existe pas
    // dans la feuille de style finale.
    return (
        <div className={`grid gap-2 ${options.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    disabled={disabled}
                    aria-pressed={value === o.value}
                    onClick={() => onPick(o.value)}
                    className={`rounded-field px-2 py-3 text-center text-sm transition-colors ${
                        value === o.value ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                    }`}
                >
                    <span className="block font-semibold">{o.label}</span>
                    {o.hint && <span className="block text-xs opacity-70">{o.hint}</span>}
                </button>
            ))}
        </div>
    );
}

function Section({ icon: Icon, title, children }: {
    icon: typeof Hand; title: string; children: React.ReactNode;
}) {
    return (
        <section className="rounded-box bg-base-200 p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Icon size={18} className="text-primary" /> {title}
            </h2>
            {children}
        </section>
    );
}

export function Preferences() {
    const { user, refreshUser } = useAuth();
    const { settings, save, unit } = useSettings();
    const { vibrate, test } = useHaptics();
    const updates = useUpdates();
    const notifs = useNotifications();
    const [pushTest, setPushTest] = useState<{ sent: number; devices: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [hapticTest, setHapticTest] = useState<HapticTest | null>(null);

    const apply = async (patch: Partial<UserSettings>): Promise<void> => {
        setError(null);
        try {
            await save(patch);
            // La main dominante vit sur le compte : la session doit être
            // rafraîchie, sinon le pavé de saisie garde l'ancienne orientation
            // jusqu'à la prochaine connexion.
            if (patch.dominantHand) await refreshUser();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        }
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <h1 className="font-display text-2xl font-bold tracking-tight">Préférences</h1>
                <p className="mt-1 text-sm opacity-60">{user?.email}</p>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}
            {saved && <div role="status" className="alert alert-success text-sm"><span>Enregistré.</span></div>}

            {/* L'index avant les réglages d'affichage : c'est une donnée de
                joueur, pas un paramètre d'application, et c'est ce qu'on vient
                mettre à jour au retour d'une compétition. */}
            <HandicapCard />

            {/* La licence SUIT l'index, et pour la même raison qu'il vient en
                premier : ce sont deux données de joueur, pas des réglages
                d'application. Dans cet ordre parce que l'index bouge à chaque
                compétition quand la licence se recopie une fois par saison. */}
            <LicenceCard />

            {/* Pas de choix de thème ici : l'application n'en a plus qu'un,
                taillé pour être lu dehors. Voir index.css pour le pourquoi. */}

            <Section icon={Hand} title="Main dominante">
                <Choice
                    options={[
                        { value: 'right', label: 'Droitier' },
                        { value: 'left', label: 'Gaucher' },
                    ]}
                    value={settings.dominantHand}
                    onPick={(v) => void apply({ dominantHand: v })}
                />
                <p className="mt-2 text-xs opacity-60">
                    Oriente l'écran de saisie : chez un gaucher, un slice part à gauche et un
                    hook à droite — les boutons et les flèches suivent.
                </p>
            </Section>

            {/* Le genre, et à quoi il sert — dit sur place.
                Deux ateliers du Pass Carte Verte ont deux barèmes : cent mètres ou
                cent trente à la mise en jeu, soixante ou quatre-vingt-dix à
                l'attaque de green. Les afficher tous les deux oblige à chercher sa
                ligne debout sur un départ.
            
                « Non précisé » est un choix de plein droit, et le défaut : les deux
                barèmes s'affichent alors, exactement comme avant que ce réglage
                existe. Ne pas répondre n'empêche rien. */}
            <Section icon={Users} title="Genre">
                <Choice
                    options={[
                        { value: 'femme', label: 'Femme' },
                        { value: 'homme', label: 'Homme' },
                        { value: 'rien', label: 'Non précisé' },
                    ]}
                    value={settings.gender ?? 'rien'}
                    onPick={(v) => void apply({ gender: v === 'rien' ? null : v })}
                />
                <p className="mt-2 text-xs opacity-60">
                    Sert uniquement à n'afficher que le barème qui vous concerne sur les
                    deux ateliers fédéraux qui en ont deux. Sans réponse, les deux sont
                    montrés.
                </p>
            </Section>

            {/* Le plafond du handicap de jeu.
                La formule fédérale des coups reçus n'en connait pas ; l'usage en
                applique un, et les applications officielles l'affichent. Il ne
                concerne que les index qui le dépassent, donc les débutants : un
                joueur à 12 ne verra jamais ce réglage agir. */}
            <Section icon={Flag} title="Coups reçus maximum">
                <Choice
                    options={[
                        { value: '36', label: '36' },
                        { value: '54', label: '54' },
                        { value: 'rien', label: 'Aucun' },
                    ]}
                    value={settings.maxHandicapStrokes == null
                        ? 'rien' : String(settings.maxHandicapStrokes)}
                    onPick={(v) => void apply({
                        maxHandicapStrokes: v === 'rien' ? null : Number(v),
                    })}
                />
                <p className="mt-2 text-xs opacity-60">
                    Limite le nombre de coups rendus sur un tour, quel que soit votre
                    index. 36 est la limite usuelle en France, et celle qu'appliquent
                    les cartes officielles : un index 54 reçoit alors deux coups par
                    trou, et non trois. « Aucun » laisse la formule seule.
                </p>
            </Section>

            <Section icon={Ruler} title="Distances">
                {/* Pas d'option « Serveur ».
                    Le réglage de l'installation est un défaut, pas un choix que
                    le joueur ait à comprendre : tant qu'il n'a rien décidé,
                    c'est simplement la bonne valeur qui apparaît sélectionnée.
                    Toucher un bouton en fait un choix explicite, qui ne bougera
                    plus si l'administrateur change le défaut. */}
                <Choice
                    options={[
                        { value: 'm', label: 'Mètres' },
                        { value: 'yd', label: 'Yards' },
                    ]}
                    value={settings.effectiveDistanceUnit}
                    onPick={(v) => void apply({ distanceUnit: v })}
                />

                <label className="mt-4 block">
                    <span className="mb-1 flex items-baseline justify-between text-sm">
                        <span>Pas du sélecteur</span>
                        <span className="tabular-nums opacity-60">
                            ± {settings.distanceStepM} {unit}
                        </span>
                    </span>
                    <input
                        type="range" min={1} max={15} step={1}
                        className="range range-primary"
                        value={settings.distanceStepM}
                        onChange={(e) => void apply({ distanceStepM: Number(e.target.value) })}
                    />
                    <span className="mt-1 block text-xs opacity-60">
                        De combien les gros boutons + et − font varier la distance pendant une
                        séance.
                    </span>
                </label>
            </Section>

            <Section icon={Smartphone} title="Retour haptique">
                <label className="flex cursor-pointer items-center gap-3">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={settings.hapticsEnabled}
                        disabled={!hapticsSupported()}
                        onChange={(e) => {
                            void apply({ hapticsEnabled: e.target.checked });
                            if (e.target.checked) vibrate('success');
                        }}
                    />
                    <span className="text-sm">
                        Vibrer à chaque saisie
                        {!hapticsSupported() && (
                            <span className="block text-xs opacity-60">
                                Non gérée par ce navigateur — iOS n'implémente pas l'API Vibration.
                            </span>
                        )}
                    </span>
                </label>

                {settings.hapticsEnabled && hapticsSupported() && (
                    <div className="mt-3">
                        <span className="mb-1 block text-sm">Intensité</span>
                        <Choice
                            options={[
                                { value: '1', label: 'Discrète' },
                                { value: '2', label: 'Normale' },
                                { value: '3', label: 'Marquée' },
                            ]}
                            value={String(settings.hapticsIntensity)}
                            onPick={(v) => {
                                void apply({ hapticsIntensity: Number(v) });
                                vibrate('tap');
                            }}
                        />
                    </div>
                )}

                {/* Un essai franc plutôt qu'un état affiché.
                    Le navigateur peut accepter l'appel et le téléphone ne rien
                    faire — silencieux vibration coupée, économie d'énergie —
                    et la page n'a aucun moyen de le savoir. Seul le poignet
                    peut trancher, donc on lui donne de quoi essayer. */}
                <button type="button" onClick={() => setHapticTest(test())}
                    className="btn btn-outline btn-sm mt-3 gap-2">
                    <Vibrate size={16} /> Tester la vibration
                </button>

                {hapticTest && (
                    <div role="status"
                        className={`alert mt-2 text-xs ${hapticTest.ok ? 'alert-info' : 'alert-warning'}`}>
                        <span>{hapticTest.ok ? hapticTest.note : hapticTest.reason}</span>
                    </div>
                )}
            </Section>

            <Section icon={BellRing} title="Notifications">
                <label className="flex cursor-pointer items-center gap-3">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={notifs.nativeEnabled}
                        disabled={!notifs.nativeSupported || notifs.nativePermission === 'denied'}
                        onChange={(e) => void notifs.setNativeEnabled(e.target.checked)}
                    />
                    <span className="text-sm">
                        Prévenir sur cet appareil
                        <span className="block text-xs opacity-60">
                            Invitations à une partie, nouveaux partenaires.
                        </span>
                    </span>
                </label>

                {/* Dire précisément ce qui marche ICI.
                    « Notifications activées » ne veut rien dire : selon le
                    navigateur, l'application installée ou non, et la
                    permission, le même réglage donne trois comportements
                    différents. Le joueur doit savoir lequel il a. */}
                <p className="mt-3 text-xs opacity-60">
                    {!notifs.nativeSupported
                        && "Ce navigateur ne gère pas les notifications."}
                    {notifs.nativeSupported && notifs.nativePermission === 'denied'
                        && "Bloquées pour ce site. À réautoriser dans les réglages du navigateur : "
                         + "l'application ne peut pas redemander."}
                    {notifs.nativeSupported && notifs.nativePermission !== 'denied'
                        && !notifs.nativeEnabled
                        && "Désactivées. La cloche continue de tout recevoir quand "
                         + "l'application est ouverte."}
                    {notifs.nativeEnabled && notifs.pushActive
                        && "Cet appareil est abonné : les notifications arrivent même "
                         + "application fermée."}
                    {notifs.nativeEnabled && !notifs.pushActive && notifs.pushSupported
                        && "Bannières affichées tant que l'application tourne. L'abonnement "
                         + "aux notifications hors application n'a pas pu être établi."}
                    {notifs.nativeEnabled && !notifs.pushActive && !notifs.pushSupported
                        && "Bannières affichées tant que l'application tourne. Pour les recevoir "
                         + "application fermée, installez-la sur l'écran d'accueil."}
                </p>

                {notifs.nativeEnabled && notifs.pushActive && (
                    <>
                        <button type="button" className="btn btn-outline btn-sm mt-3 gap-2"
                            onClick={() => void notifs.testPush().then(setPushTest)}>
                            <BellRing size={16} /> Envoyer un essai
                        </button>
                        {pushTest && (
                            <div role="status" className="alert alert-info mt-2 text-xs">
                                <span>
                                    {pushTest.sent > 0
                                        ? `Envoyé à ${pushTest.sent} appareil${pushTest.sent > 1 ? 's' : ''} sur `
                                          + `${pushTest.devices}. Si rien n'apparaît, la bannière a été retenue par le `
                                          + `téléphone — ou l'application est au premier plan, auquel cas c'est voulu.`
                                        : `Aucun appareil n'a accepté l'envoi (${pushTest.devices} abonné${pushTest.devices > 1 ? 's' : ''}).`}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </Section>

            <Section icon={RefreshCw} title="Version">
                <p className="font-mono text-xs opacity-70">{updates.version}</p>

                {/* Le bouton ne promet rien qu'il ne sache tenir : il interroge
                    le serveur et rapporte ce qu'il a trouvé. « À jour » n'est
                    affiché que si la recherche a abouti — hors ligne, on ne
                    sait pas, et le dire vaut mieux que rassurer à tort. */}
                <button type="button" className="btn btn-outline btn-sm mt-3 gap-2"
                    disabled={updates.lastCheck.state === 'checking'}
                    onClick={() => void updates.check()}>
                    <RefreshCw size={16} className={
                        updates.lastCheck.state === 'checking' ? 'animate-spin' : ''} />
                    Rechercher une mise à jour
                </button>

                {updates.ready ? (
                    <div className="mt-3 space-y-2">
                        <div role="status" className="alert alert-info text-xs">
                            <span>Une nouvelle version est prête.</span>
                        </div>
                        <button type="button" onClick={updates.apply}
                            className="btn btn-primary btn-block gap-2 tap-target">
                            <RefreshCw size={18} /> Mettre à jour maintenant
                        </button>
                    </div>
                ) : (
                    <p className="mt-2 text-xs opacity-60">
                        {updates.lastCheck.state === 'up-to-date'
                            && 'Vous utilisez la dernière version.'}
                        {updates.lastCheck.state === 'found'
                            && 'Mise à jour trouvée, préparation en cours…'}
                        {updates.lastCheck.state === 'unsupported'
                            && "Les mises à jour automatiques demandent l'application installée sur "
                            + "l'écran d'accueil. Dans un onglet, il suffit de recharger la page."}
                        {updates.lastCheck.state === 'idle'
                            && "L'application vérifie d'elle-même au démarrage et à chaque retour à l'écran."}
                        {updates.lastCheck.state === 'checking' && 'Recherche en cours…'}
                    </p>
                )}
            </Section>

            <Section icon={Sun} title="Écran">
                <label className="flex cursor-pointer items-center gap-3">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={settings.wakeLockEnabled}
                        disabled={!wakeLockSupported()}
                        onChange={(e) => void apply({ wakeLockEnabled: e.target.checked })}
                    />
                    <span className="text-sm">
                        Garder l'écran allumé pendant une séance
                        {!wakeLockSupported() && (
                            <span className="block text-xs opacity-60">Non gérée par ce navigateur</span>
                        )}
                    </span>
                </label>
            </Section>
        </div>
    );
}
